import React, { useState, useEffect } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import { format, addDays } from 'date-fns';
import get from 'lodash.get';
import { Button, Flex, Box, SelectDropdown, Switch, Typography, TextInput } from '@kistech/kis-ui';
import { Typography as MuiTypography, Box as MuiBox, Grid } from '@mui/material';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { actions as clientActions, selectors as clientsSelectors, Client, SalesReportSettings } from 'store/clients';
import { actions as configActions, selectors as configSelectors, Configuration, Ticketing } from 'store/configurations';
import { actions as settingsActions, selectors as settingsSelectors, KioskSales } from 'store/settings';
import { exportKioskSalesReport } from 'utils/sftKioskSalesReport';
import { getSortedList } from 'utils/stringFormat';
import { useStyles } from './styled';

const SettingsView: React.FC<RouteComponentProps> = () => {
  const classes = useStyles();

  // ACTIONS AND SELECTORS
  const {
    resetClientAction,
    invalidateCache,
    getConfigurations,
    updateConfiguration,
    getSalesByKiosk,
    setConfigurationId,
    resetSettingsAction,
    resetSalesByKiosk
  } = useActions({
    resetClientAction: clientActions.resetAction,
    invalidateCache: clientActions.invalidateCache,
    getConfigurations: configActions.getConfigurations,
    updateConfiguration: configActions.updateConfiguration,
    getSalesByKiosk: settingsActions.getSalesByKiosk,
    setConfigurationId: settingsActions.setConfigurationId,
    resetSettingsAction: settingsActions.resetAction,
    resetSalesByKiosk: settingsActions.resetSalesByKiosk
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const clientAction = useSelector(clientsSelectors.selectAction);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const configurationId = useSelector(settingsSelectors.selectConfigurationId);
  const salesByKiosk = useSelector(settingsSelectors.selectSalesByKiosk);
  const settingsAction = useSelector(settingsSelectors.selectAction);

  const [salesReportDate, setSalesReportDate] = useState<string>(format(new Date(), 'MM-dd-yyyy'));
  const [isSalesReportDateValid, setIsSalesReportDateValid] = useState<boolean>(true);
  const [salesReportStatus, setSalesReportStatus] = useState<string>('');
  const [salesReportDayOfEvent, setSalesReportDayOfEvent] = useState<number>(0);
  const [salesReportCurrentYearDate, setSalesReportCurrentYearDate] = useState<string>('');
  const [salesReportPreviousYearDate, setSalesReportPreviousYearDate] = useState<string>('');
  const [oneDayCurrentYearSalesByKiosk, setOneDayCurrentYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [currentYearSalesByKiosk, setCurrentYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [oneDayPreviousYearSalesByKiosk, setOneDayPreviousYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [previousYearSalesByKiosk, setPreviousYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [lookupValue, setLookupValue] = useState<boolean | undefined>();
  const [creationValue, setCreationValue] = useState<boolean | undefined>(false);
  const [surveyPageValue, setSurveyPageValue] = useState<boolean | undefined>(false);
  const [donationPopupValue, setDonationPopupValue] = useState<boolean | undefined>(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);

  const salesReportSettings: SalesReportSettings = get(client, 'configuration.salesReportSettings', {});
  const ticketing = get(selectedConfiguration, 'ticketing');
  const customerIdentification = get(ticketing, 'customerIdentification');
  const customerLookup = get(customerIdentification, 'lookup');
  const customerCreation = get(customerIdentification, 'creation');
  const survey = get(ticketing, 'survey');
  const donationOverlay = get(ticketing, 'donations.overlay');

  useEffect(() => {
    getConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (configurations.length && !configurationId) {
      setConfigurationId(configurations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  useEffect(() => {
    let configuration = null;
    if (configurationId && configurations.length) {
      configuration = configurations.find((c) => c.id === configurationId);
      if (!configuration) configuration = configurations[0];
    }
    setSelectedConfiguration(configuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId, configurations]);

  useEffect(() => {
    if (selectedConfiguration) {
      setLookupValue(customerLookup && customerLookup.enabled);
      setCreationValue(customerCreation && customerCreation.enabled);
      setSurveyPageValue(survey && survey.enabled);
      setDonationPopupValue(donationOverlay && donationOverlay.enabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfiguration]);

  // Building the kiosk sales report for SFT
  useEffect(() => {
    if (salesByKiosk && salesReportStatus === 'oneDayCurrentYear') {
      // Save the response for the 1st date range (out of 4)
      setOneDayCurrentYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('oneDayPreviousYear');
      resetSalesByKiosk();
      // Get sales for the 2nd date range (out of 4)
      getSalesByKiosk({
        startDate: salesReportPreviousYearDate,
        endDate: salesReportPreviousYearDate
      });
    } else if (salesByKiosk && salesReportStatus === 'oneDayPreviousYear') {
      // Save the response for the 2nd date range (out of 4)
      setOneDayPreviousYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('currentYear');
      resetSalesByKiosk();
      // Get sales for the 3rd date range (out of 4)
      getSalesByKiosk({
        startDate: salesReportSettings.currentEventStartDate,
        endDate: salesReportCurrentYearDate
      });
    } else if (salesByKiosk && salesReportStatus === 'currentYear') {
      // Save the response for the 3rd date range (out of 4)
      setCurrentYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('previousYear');
      resetSalesByKiosk();
      // Get sales for the 4th date range (out of 4)
      getSalesByKiosk({
        startDate: salesReportSettings.previousEventStartDate,
        endDate: salesReportPreviousYearDate
      });
    } else if (salesByKiosk && salesReportStatus === 'previousYear') {
      // Save the response for the 4th date range (out of 4)
      setPreviousYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('exportData');
      resetSalesByKiosk();
    } else if (salesReportStatus === 'exportData') {
      setSalesReportStatus('');
      // Generate the xls file and download
      exportKioskSalesReport({
        salesReportSettings,
        salesReportCurrentYearDate,
        salesReportPreviousYearDate,
        salesReportDayOfEvent,
        oneDayCurrentYearSalesByKiosk,
        oneDayPreviousYearSalesByKiosk,
        currentYearSalesByKiosk,
        previousYearSalesByKiosk
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesByKiosk]);

  // Validate and update the sales report date when the input value changes
  const onSalesReportDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const value = e.target.value.trim();

    // Validate the format (MM-DD-YYYY)
    const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;
    const isValidDate = dateRegex.test(value);

    // Set the input value and the valid status that enables/disables the button
    setSalesReportDate(value);
    setIsSalesReportDateValid(isValidDate);
  };

  // Start generating the sales report, triggering a useEffect() to complete it
  const onBuildReportClick = () => {
    const { currentEventStartDate, previousEventStartDate } = salesReportSettings;

    try {
      // Reformat the input date
      const currentYearReportDate = format(new Date(salesReportDate), 'yyyy-MM-dd');
      setSalesReportCurrentYearDate(currentYearReportDate);

      // Get the difference (in days) from the event start date that is configured
      const date1 = new Date(`${currentEventStartDate}T00:00:00`);
      const date2 = new Date(`${currentYearReportDate}T00:00:00`);
      const diffInTime = date2.getTime() - date1.getTime();
      const diffInDays = Math.round(diffInTime / (1000 * 3600 * 24));

      if (diffInDays < 0) {
        setErrorMessage(`${salesReportDate} is not a valid date for this report`);
      } else {
        // Use the difference to determine the date of the previous year
        const previousYearSameDayOfEvent = addDays(new Date(`${previousEventStartDate}T00:00:00`), diffInDays);
        setSalesReportPreviousYearDate(format(previousYearSameDayOfEvent, 'yyyy-MM-dd'));
        setSalesReportDayOfEvent(diffInDays + 1);

        // Get sales for the 1st date range (out of 4)
        setSalesReportStatus('oneDayCurrentYear');
        getSalesByKiosk({
          startDate: currentYearReportDate,
          endDate: currentYearReportDate
        });
      }
    } catch (err) {
      setErrorMessage('Unable to determine the date range for the report');
    }
  };

  const onErrorOverlayClick = () => {
    if (clientAction.error) resetClientAction();
    if (settingsAction.error) resetSettingsAction();
    if (errorMessage) setErrorMessage('');
  };

  if (!client) return <Redirect to="/clients" />;

  return (
    <>
      {(clientAction.error || settingsAction.error || errorMessage) && (
        <ActionOverlay
          title={
            <MuiTypography className={classes.errorOverlayHeader} style={{ lineHeight: 1, fontWeight: 600 }}>
              {clientAction.error || settingsAction.error || errorMessage}
            </MuiTypography>
          }
          confirmText="Ok"
          onConfirm={onErrorOverlayClick}
          width={800}
          height={350}
        />
      )}
      <>
        {/* Page header */}
        <Grid className={classes.settingsHeading}>
          <Box flexOverride="none">
            <MuiTypography className={classes.settingsHeaderText}>Settings</MuiTypography>
          </Box>
        </Grid>
        {/* Clear cache button */}
        <Flex align="center" fullWidth style={{ margin: '0px 40px 48px 40px' }}>
          <MuiTypography style={{ margin: '0 20px 0 0', fontSize: '18px' }}>Clear cached event data</MuiTypography>
          <Button size="small" isLoading={clientAction.pending} onClick={invalidateCache}>
            Clear Cache
          </Button>
        </Flex>
        {/* Kiosk Sales Report (SFT only) */}
        {client.configuration.salesReportSettings && (
          <Flex direction="column" fullWidth style={{ margin: '0px 40px 48px 40px' }}>
            <Typography size={24} weight={700}>
              Kiosk Sales Report
            </Typography>
            <Flex style={{ margin: '24px 0 0' }}>
              <MuiBox>
                <TextInput
                  label="Date (MM-DD-YYYY)"
                  size="medium"
                  placeholder="MM-DD-YYYY"
                  value={salesReportDate}
                  style={{ height: '45px' }}
                  onChange={(e) => onSalesReportDateInputChange(e)}
                  data-testid="kiosk-sales-report-date-input"
                />
              </MuiBox>
              <Button
                size="small"
                isDisabled={!isSalesReportDateValid}
                isLoading={settingsAction.pending}
                onClick={onBuildReportClick}
                className={classes.salesReportButton}
                data-testid="build-report-button"
              >
                Build Report
              </Button>
            </Flex>
          </Flex>
        )}
        {/* Configuration selection */}
        <Flex fullWidth align="center" style={{ margin: '0px 40px 0px 40px' }}>
          <Typography size={24} weight={700}>
            Configuration
          </Typography>
          <Box width={20} />
          <SelectDropdown
            items={getSortedList(configurations, 'name')}
            itemToString={(item: Configuration) => item && item.name}
            selectedItem={selectedConfiguration as Configuration}
            onChange={(item: Configuration) => setConfigurationId(item.id)}
            data-testid="configuration-selector"
          />
        </Flex>
        {/* Customer Id subheader */}
        <Grid className={classes.settingsHeading}>
          <Box flexOverride="none">
            <MuiTypography
              className={classes.customerIdentification}
              style={{ color: !customerLookup && !customerCreation ? 'grey' : 'black' }}
            >
              Customer Identification
            </MuiTypography>
          </Box>
        </Grid>
        {/* Customer Id lookup toggle */}
        <Flex fullWidth align="center" style={{ margin: '0px 40px 24px 40px' }}>
          <MuiTypography className={classes.lookupText} style={{ color: !customerLookup ? 'grey' : 'black' }}>
            Lookup
          </MuiTypography>
          <Switch
            checked={lookupValue === true}
            checkedLabel="Enabled"
            uncheckedLabel="Disabled"
            isDisabled={!customerLookup}
            onChange={(e) => {
              setLookupValue(e.target.checked);
              updateConfiguration({
                id: (selectedConfiguration as Configuration).id,
                ticketing: {
                  ...ticketing,
                  customerIdentification: {
                    ...customerIdentification,
                    lookup: {
                      ...customerLookup,
                      enabled: e.target.checked.toString()
                    }
                  }
                }
              });
            }}
            data-testid="customer-lookup-switch"
          />
        </Flex>
        {/* Customer Id creation toggle */}
        <Flex fullWidth align="center" style={{ margin: '24px 40px' }}>
          <MuiTypography className={classes.creationText} style={{ color: !customerCreation ? 'grey' : 'black' }}>
            Creation
          </MuiTypography>
          <Switch
            checked={creationValue === true}
            checkedLabel="Enabled"
            uncheckedLabel="Disabled"
            isDisabled={!customerCreation}
            onChange={(e) => {
              setCreationValue(e.target.checked);
              updateConfiguration({
                id: (selectedConfiguration as Configuration).id,
                ticketing: {
                  ...ticketing,
                  customerIdentification: {
                    ...customerIdentification,
                    creation: {
                      ...customerCreation,
                      enabled: e.target.checked.toString()
                    }
                  }
                }
              });
            }}
            data-testid="customer-creation-switch"
          />
        </Flex>
        {/* Survey page toggle */}
        <Grid style={{ padding: '0px 40px' }}>
          <Box flexOverride="none">
            <Flex fullWidth align="center">
              <MuiTypography className={classes.surveyPageText} style={{ color: !survey ? 'grey' : 'black' }}>
                Survey Page
              </MuiTypography>
              <Switch
                checked={surveyPageValue === true}
                checkedLabel="Enabled"
                isDisabled={!survey}
                uncheckedLabel="Disabled"
                onChange={(e) => {
                  setSurveyPageValue(e.target.checked);
                  updateConfiguration({
                    id: (selectedConfiguration as Configuration).id,
                    ticketing: {
                      ...ticketing,
                      survey: {
                        ...survey,
                        enabled: e.target.checked.toString()
                      }
                    }
                  });
                }}
                data-testid="survey-switch"
              />
            </Flex>
          </Box>
        </Grid>
        {/* Donation overlay toggle */}
        <Grid style={{ padding: '0px 40px' }}>
          <Box flexOverride="none">
            <Flex fullWidth align="center">
              <MuiTypography className={classes.donationOverlay} style={{ color: !donationOverlay ? 'grey' : 'black' }}>
                Donation Pop-up
              </MuiTypography>
              <Switch
                checked={donationPopupValue === true}
                checkedLabel="Enabled"
                isDisabled={!donationOverlay}
                uncheckedLabel="Disabled"
                onChange={(e) => {
                  setDonationPopupValue(e.target.checked);
                  updateConfiguration({
                    id: (selectedConfiguration as Configuration).id,
                    ticketing: {
                      ...ticketing,
                      donations: {
                        ...(ticketing as Ticketing).donations,
                        overlay: {
                          ...get(ticketing, 'donations.overlay'),
                          enabled: e.target.checked.toString()
                        }
                      }
                    }
                  });
                }}
                data-testid="donation-roundup-switch"
              />
            </Flex>
          </Box>
        </Grid>
      </>
    </>
  );
};

export default SettingsView;
